import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Gift, PartyPopper, ShieldCheck, Sparkles } from "lucide-react";
import { api, ApiError, formatMoney } from "../lib/api.js";
import { formatBillingDayAndTime } from "../lib/formatBillingDate.js";
import type { CancellationReason } from "../../../shared/cancellation_reasons.js";
import type { CancelRescueAssets } from "../lib/cancellationLosses.js";
import {
  premiumTrialExpiresAt,
  retentionVideoEmbedUrl,
  retentionVideoIsLandscape,
  specialistDeliverBy,
  SPECIALIST_NOTES_MAX,
  SPECIALIST_REFERENCE_MONTHLY_CENTS,
  upgradeOfferKind,
  type RetentionGiftOffer,
  type RetentionLadderOfferKind,
  type RetentionLadderOffers,
  type RetentionLadderStep,
} from "../../../shared/retention_ladder.js";

/** What the closing screen shows once the customer took something: a celebration for a gift. */
type Kept =
  | { kind: "gift"; name: string; priceCents: number | null; priceOnceCents: number | null; openUrl: string | null }
  | { kind: "premium"; until: string; priceCents: number | null; openUrl: string | null }
  | { kind: "plain"; title: string; body: string };

/**
 * The retention ladder (shared/retention_ladder.ts): one screen per step, each with one offer and
 * "Não, obrigado" to move on. Taking an offer ends the dialog with the customer staying; declining
 * the last step hands back to the confirm screen. Every screen, answer and grant is audited.
 *
 * A gift reads as a gift (owner, 19/10): what it does for the business first, then what it costs
 * (the catalog's monthly price, struck through), that it is offered once, and a celebration with
 * "Abrir agora" when taken, since the gain is felt when it is used.
 */
export function RetentionLadder({
  token,
  attemptId,
  steps,
  offers,
  assets,
  reason,
  reasonLabel,
  refundableUntil,
  onContinueCancelling,
  onClose,
}: {
  token: string;
  /** The experiment's attempt, sent with every answer (server/retention_attempts.ts). */
  attemptId: string | null;
  /** From retentionLadderSteps, computed once when the ladder opened. */
  steps: RetentionLadderStep[];
  offers: RetentionLadderOffers;
  /** What the customer built (PIT-443), summed up on the first screen; null when unknown. */
  assets: CancelRescueAssets | null;
  reason: CancellationReason;
  /** The reason and subreasons as the customer read them, for support. */
  reasonLabel: string;
  /** The refund deadline, null outside the 7-day window. */
  refundableUntil: string | null;
  onContinueCancelling: () => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";
  // Prices come from the BRL catalog: only shown to whoever pays in reais.
  const showPrices = locale.startsWith("pt");
  const [index, setIndex] = useState(0);
  const [kept, setKept] = useState<Kept | null>(null);
  const [writingNotes, setWritingNotes] = useState(false);
  const [autopilotConsent, setAutopilotConsent] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const viewedRef = useRef(new Set<RetentionLadderStep>());
  const acceptedStepRef = useRef<RetentionLadderStep | null>(null);

  const step = steps[index];
  const now = new Date();
  const deadline = refundableUntil ? new Date(refundableUntil) : null;
  const dateAtTime = (value: Date | string) => t("integrated.dateAtTime", formatBillingDayAndTime(value, locale));
  const deadlineLabel = deadline ? dateAtTime(deadline) : "";
  const upgrade = upgradeOfferKind(offers);
  const product = offers.product;
  const money = (cents: number) => formatMoney(cents, locale, "BRL");
  const attempt = attemptId ?? undefined;

  function offerOf(s: RetentionLadderStep): { offer: RetentionLadderOfferKind; offer_key?: string } {
    if (s === "guarantee") return { offer: "keep_using" };
    if (s === "bonus") return { offer: "bonus", offer_key: offers.bonus?.key };
    if (s === "upgrade") {
      return upgrade === "product" ? { offer: "product", offer_key: product?.key } : { offer: "premium_trial" };
    }
    if (s === "autopilot") return { offer: "autopilot" };
    return { offer: "specialist" };
  }

  function record(event: "step_viewed" | "offer_accepted" | "offer_declined" | "gift_opened", s: RetentionLadderStep) {
    void api("/api/integrated/cancel-rescue/ladder-events", {
      method: "POST",
      // gift_opened goes out as the top window navigates away, which would cancel a plain fetch.
      keepalive: event === "gift_opened",
      body: JSON.stringify({
        token,
        event,
        step: s,
        ...offerOf(s),
        cancellation_reason: reason,
        in_refund_window: deadline !== null,
        retention_attempt_id: attempt,
      }),
    }).catch(() => undefined);
  }

  useEffect(() => {
    if (!step || kept || viewedRef.current.has(step)) return;
    viewedRef.current.add(step);
    record("step_viewed", step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, kept]);

  function next() {
    setError(null);
    setWritingNotes(false);
    if (index + 1 >= steps.length) {
      onContinueCancelling();
      return;
    }
    setIndex(index + 1);
  }

  function decline() {
    record("offer_declined", step);
    next();
  }

  function accepted() {
    acceptedStepRef.current = step;
    record("offer_accepted", step);
  }

  /** Zenpost said no (given meanwhile, or the customer has it): skip the step quietly. */
  function skipIfNotEligible(err: unknown): boolean {
    if (err instanceof ApiError && err.status === 409) {
      next();
      return true;
    }
    return false;
  }

  async function acceptGift(gift: RetentionGiftOffer) {
    setBusy(true);
    setError(null);
    try {
      await api<{ key: string; expires_at: string | null }>("/api/integrated/cancel-rescue/retention-bonus", {
        method: "POST",
        body: JSON.stringify({ token, cancellation_reason: reason, feature_key: gift.key, retention_attempt_id: attempt }),
      });
      accepted();
      setKept({
        kind: "gift",
        name: giftName(gift),
        priceCents: gift.price_monthly_cents ?? null,
        priceOnceCents: gift.price_once_cents ?? null,
        openUrl: gift.open_url ?? null,
      });
    } catch (err) {
      if (!skipIfNotEligible(err)) setError(t("integrated.retentionLadderError"));
    } finally {
      setBusy(false);
    }
  }

  async function acceptPremium() {
    setBusy(true);
    setError(null);
    try {
      const trial = await api<{ keys: string[]; expires_at: string }>("/api/integrated/cancel-rescue/premium-trial", {
        method: "POST",
        body: JSON.stringify({ token, cancellation_reason: reason, retention_attempt_id: attempt }),
      });
      accepted();
      setKept({
        kind: "premium",
        until: dateAtTime(trial.expires_at),
        priceCents: offers.premium_trial?.price_monthly_cents ?? null,
        openUrl: offers.premium_trial?.open_url ?? null,
      });
    } catch (err) {
      if (!skipIfNotEligible(err)) setError(t("integrated.retentionLadderError"));
    } finally {
      setBusy(false);
    }
  }

  async function sendToSpecialist() {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ deliver_by: string }>("/api/integrated/cancel-rescue/specialist", {
        method: "POST",
        body: JSON.stringify({
          token,
          cancellation_reason: reason,
          reason_label: reasonLabel || undefined,
          notes: notes.trim() || undefined,
          retention_attempt_id: attempt,
        }),
      });
      accepted();
      setKept({
        kind: "plain",
        title: t("integrated.retentionLadderKeptSpecialistTitle"),
        body: t("integrated.retentionLadderKeptSpecialistBody", { deliverBy: dateAtTime(r.deliver_by) }),
      });
    } catch (err) {
      // The deadline moved past the delivery day while the screen was open: nothing to promise.
      if (err instanceof ApiError && err.status === 409) {
        onContinueCancelling();
        return;
      }
      setError(t("integrated.retentionLadderError"));
    } finally {
      setBusy(false);
    }
  }

  async function chooseAutopilot() {
    setBusy(true);
    setError(null);
    try {
      await api<{ notified: boolean }>("/api/integrated/cancel-rescue/autopilot", {
        method: "POST",
        body: JSON.stringify({
          token,
          cancellation_reason: reason,
          reason_label: reasonLabel || undefined,
          retention_attempt_id: attempt,
        }),
      });
      accepted();
      setKept({
        kind: "plain",
        title: t("integrated.retentionLadderKeptAutopilotTitle"),
        body: t("integrated.retentionLadderKeptAutopilotBody"),
      });
    } catch (err) {
      if (!skipIfNotEligible(err)) setError(t("integrated.retentionLadderError"));
    } finally {
      setBusy(false);
    }
  }

  function keepUsing() {
    accepted();
    setKept({
      kind: "plain",
      title: t("integrated.retentionLadderKeptKeepUsingTitle"),
      body: t("integrated.retentionLadderKeptKeepUsingBody", { deadline: deadlineLabel }),
    });
  }

  /**
   * Takes the customer to the gift in app.zenpost.ai. This page is usually the app's billing
   * iframe, which may navigate the top window after a tap (allow-top-navigation-by-user-activation).
   */
  function openInApp(url: string) {
    if (acceptedStepRef.current) record("gift_opened", acceptedStepRef.current);
    try {
      if (window.top && window.top !== window.self) {
        window.top.location.href = url;
        return;
      }
    } catch {
      window.open(url, "_blank", "noopener");
      return;
    }
    window.location.href = url;
  }

  function giftName(gift: RetentionGiftOffer): string {
    return t(`integrated.bonusName_${gift.key}`, { defaultValue: gift.name });
  }

  if (kept) return <Celebration kept={kept} money={showPrices ? money : null} onOpen={openInApp} onClose={onClose} />;
  if (!step) return null;

  const guaranteeBadge = deadline && step !== "guarantee" && (
    <div className="flex items-center gap-2 rounded-lg border border-success/35 bg-success/[0.08] px-3 py-2 text-xs font-medium">
      <ShieldCheck className="w-4 h-4 shrink-0 text-success" />
      {t("integrated.retentionLadderGuaranteeBadge", { deadline: deadlineLabel })}
    </div>
  );

  // What they built so far, once, on the first screen: what a gift adds to, and what leaving drops.
  const built = index === 0 && assets && <BuiltSummary assets={assets} />;

  const declineButton = (label = t("integrated.retentionLadderNoThanks")) => (
    <button type="button" className="btn-ghost w-full" disabled={busy} onClick={decline}>
      {label}
    </button>
  );

  const video = (key: string) => {
    const src = retentionVideoEmbedUrl(key);
    if (!src) return null;
    return (
      <div className="flex flex-col items-center gap-1">
        <div className="text-xs font-medium text-ink-2">{t(`integrated.retentionLadderVideoCaption_${key}`, {
            defaultValue: t("integrated.retentionLadderVideoCaption"),
          })}</div>
        <div
          className={`w-full overflow-hidden rounded-xl bg-black ${
            retentionVideoIsLandscape(key) ? "aspect-video" : "max-w-[220px] aspect-[9/16]"
          }`}
        >
          <iframe
            src={src}
            title={t("integrated.retentionLadderVideoTitle")}
            className="w-full h-full"
            loading="lazy"
            allow="encrypted-media; picture-in-picture"
            // YouTube refuses to play without a referrer (error 153), and helmet's page policy sends none.
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </div>
    );
  };

  const errorLine = error && <p className="text-sm text-danger">{error}</p>;

  if (step === "guarantee" && deadline) {
    const daysLeft = Math.max(0, Math.ceil((deadline.getTime() - now.getTime()) / 86_400_000));
    return (
      <>
        <div className="flex items-start gap-2 pr-8">
          <ShieldCheck className="w-6 h-6 shrink-0 text-success" />
          <div className="text-lg font-semibold">
            {t("integrated.retentionLadderGuaranteeTitle", { deadline: deadlineLabel })}
          </div>
        </div>
        <p className="text-sm text-ink-2">{t("integrated.retentionLadderGuaranteeBody", { count: daysLeft })}</p>
        {built}
        <div className="flex flex-col gap-2">
          <button type="button" className="btn-primary w-full" onClick={keepUsing}>
            {t("integrated.retentionLadderKeepUsing")}
          </button>
          {declineButton()}
        </div>
      </>
    );
  }

  const giftScreen = (gift: RetentionGiftOffer, cta: string, isNew: boolean) => (
    <>
      {guaranteeBadge}
      {built}
      <GiftCard
        badge={t(isNew ? "integrated.retentionLadderGiftBadgeNew" : "integrated.retentionLadderGiftBadge")}
        headline={t(`integrated.retentionLadderGiftHeadline_${gift.key}`, { defaultValue: giftName(gift) })}
        name={giftName(gift)}
        body={t(`integrated.retentionLadderGiftBody_${gift.key}`, { defaultValue: "" })}
        priceLine={
          showPrices && gift.price_once_cents ? (
            <PriceLine
              was={money(gift.price_once_cents)}
              now={t("integrated.retentionLadderGiftFree")}
              until={t("integrated.retentionLadderGiftWhileSubscribed")}
              savings={t(`integrated.retentionLadderGiftSoldFor_${gift.key}`, {
                price: money(gift.price_once_cents),
                defaultValue: "",
              })}
            />
          ) : showPrices && gift.price_monthly_cents ? (
            <PriceLine
              was={t("integrated.retentionLadderGiftPriceWas", { price: money(gift.price_monthly_cents) })}
              now={t("integrated.retentionLadderGiftFree")}
              until={t("integrated.retentionLadderGiftWhileSubscribed")}
              savings={t("integrated.retentionLadderGiftSavings", { price: money(gift.price_monthly_cents) })}
            />
          ) : (
            <p className="text-sm font-semibold">{t("integrated.retentionLadderGiftFreeWhileSubscribed")}</p>
          )
        }
        once={t("integrated.retentionLadderGiftOnce")}
      />
      {video(gift.key)}
      {errorLine}
      <div className="flex flex-col gap-2">
        <button type="button" className="btn-primary w-full" disabled={busy} onClick={() => void acceptGift(gift)}>
          {busy ? t("integrated.cancelBusy") : cta}
        </button>
        {declineButton()}
      </div>
    </>
  );

  if (step === "bonus" && offers.bonus) {
    return giftScreen(offers.bonus, t("integrated.retentionLadderBonusCta"), false);
  }

  if (step === "upgrade" && upgrade === "premium_trial") {
    const until = dateAtTime(premiumTrialExpiresAt(now, deadline));
    const priceCents = offers.premium_trial?.price_monthly_cents ?? null;
    return (
      <>
        {guaranteeBadge}
        {built}
        <GiftCard
          badge={t("integrated.retentionLadderGiftBadge")}
          headline={t("integrated.retentionLadderPremiumTitle")}
          name="Premium"
          body={t("integrated.retentionLadderPremiumBody", { until })}
          priceLine={
            showPrices && priceCents ? (
              <PriceLine
                was={t("integrated.retentionLadderPremiumPriceWas", { price: money(priceCents) })}
                now={t("integrated.retentionLadderGiftFree")}
                until={t("integrated.retentionLadderPremiumFreeUntil", { until })}
              />
            ) : (
              <p className="text-sm font-semibold">{t("integrated.retentionLadderPremiumFreeUntil", { until })}</p>
            )
          }
          once={t("integrated.retentionLadderPremiumOnce")}
        />
        {errorLine}
        <div className="flex flex-col gap-2">
          <button type="button" className="btn-primary w-full" disabled={busy} onClick={() => void acceptPremium()}>
            {busy ? t("integrated.cancelBusy") : t("integrated.retentionLadderPremiumCta")}
          </button>
          {declineButton()}
        </div>
      </>
    );
  }

  if (step === "upgrade" && upgrade === "product" && product) {
    return giftScreen(product, t("integrated.retentionLadderProductCta", { name: giftName(product) }), true);
  }

  if (step === "autopilot") {
    return (
      <>
        {guaranteeBadge}
        {built}
        <div className="text-lg font-semibold pr-8">{t("integrated.retentionLadderAutopilotTitle")}</div>
        <p className="text-sm text-ink-2">{t("integrated.retentionLadderAutopilotBody")}</p>
        <div className="flex items-start gap-2 rounded-lg bg-purple-deep/5 px-3 py-2 text-sm">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-purple-deep" />
          <span>{t("integrated.retentionLadderAutopilotPrice")}</span>
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={autopilotConsent}
            onChange={(e) => setAutopilotConsent(e.target.checked)}
          />
          <span>{t("integrated.retentionLadderAutopilotConsent")}</span>
        </label>
        {errorLine}
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="btn-primary w-full"
            disabled={busy || !autopilotConsent}
            onClick={() => void chooseAutopilot()}
          >
            {busy ? t("integrated.cancelBusy") : t("integrated.retentionLadderAutopilotCta")}
          </button>
          {declineButton(t("integrated.retentionLadderNoThanks"))}
        </div>
      </>
    );
  }

  if (step === "specialist") {
    const deliverBy = dateAtTime(specialistDeliverBy(now));
    if (writingNotes) {
      return (
        <>
          {guaranteeBadge}
          <div className="text-lg font-semibold pr-8">{t("integrated.retentionLadderNotesTitle")}</div>
          <textarea
            className="input w-full min-h-[96px]"
            maxLength={SPECIALIST_NOTES_MAX}
            placeholder={t("integrated.retentionLadderNotesPlaceholder")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {errorLine}
          <div className="flex flex-col gap-2">
            <button type="button" className="btn-primary w-full" disabled={busy} onClick={() => void sendToSpecialist()}>
              {busy ? t("integrated.cancelBusy") : t("integrated.retentionLadderNotesSend")}
            </button>
            <button type="button" className="btn-ghost w-full" disabled={busy} onClick={() => setWritingNotes(false)}>
              {t("integrated.retentionLadderBack")}
            </button>
          </div>
        </>
      );
    }
    return (
      <>
        {guaranteeBadge}
        {built}
        <div className="text-lg font-semibold pr-8">{t("integrated.retentionLadderSpecialistTitle")}</div>
        <p className="text-sm text-ink-2">{t("integrated.retentionLadderSpecialistBody", { deliverBy })}</p>
        <div className="flex items-start gap-2 rounded-lg bg-purple-deep/5 px-3 py-2 text-sm">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-purple-deep" />
          <span>
            {t("integrated.retentionLadderSpecialistValue")}
            {showPrices && SPECIALIST_REFERENCE_MONTHLY_CENTS
              ? ` ${t("integrated.retentionLadderSpecialistReference", { price: money(SPECIALIST_REFERENCE_MONTHLY_CENTS) })}`
              : ""}
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <button type="button" className="btn-primary w-full" onClick={() => setWritingNotes(true)}>
            {t("integrated.retentionLadderSpecialistCta")}
          </button>
          {declineButton(t("integrated.retentionLadderSpecialistDecline"))}
        </div>
      </>
    );
  }

  return null;
}

/** The gift itself: a framed card with the badge, what it does, its price and that it comes once. */
function GiftCard({
  badge,
  headline,
  name,
  body,
  priceLine,
  once,
}: {
  badge: string;
  headline: string;
  name: string;
  body: string;
  priceLine: React.ReactNode;
  once: string;
}) {
  return (
    <div className="rounded-[var(--r-lg)] p-[2px]" style={{ background: "var(--grad-hero)" }}>
      <div className="flex flex-col gap-3 rounded-[calc(var(--r-lg)-2px)] bg-white p-4">
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold text-white"
            style={{ background: "var(--grad-primary)" }}
          >
            <Gift className="w-3.5 h-3.5" />
            {badge}
          </span>
          <span className="text-xs font-semibold text-ink-2">{name}</span>
        </div>
        <div className="text-lg font-semibold leading-snug pr-6">{headline}</div>
        {body && <p className="text-sm text-ink-2">{body}</p>}
        {priceLine}
        <p className="flex items-center gap-1.5 text-xs text-ink-2">
          <Sparkles className="w-3.5 h-3.5 shrink-0 text-purple-deep" />
          {once}
        </p>
      </div>
    </div>
  );
}

/** "~~R$ 7,00/mês~~ Grátis enquanto você for assinante", and what it saves. */
function PriceLine({ was, now, until, savings }: { was: string; now: string; until: string; savings?: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex flex-wrap items-baseline gap-x-2">
        <s className="text-sm text-ink-2">{was}</s>
        <span className="text-xl font-bold text-success">{now}</span>
        <span className="text-sm text-ink-2">{until}</span>
      </div>
      {savings && <div className="text-xs font-semibold text-success">{savings}</div>}
    </div>
  );
}

/** What the customer built in Zenpost, in one line each; nothing when they built nothing yet. */
function BuiltSummary({ assets }: { assets: CancelRescueAssets }) {
  const { t } = useTranslation();
  const lines = [
    assets.contents > 0 && t("integrated.retentionLadderBuiltContents", { count: assets.contents }),
    (assets.scheduled_contents ?? 0) > 0 &&
      t("integrated.retentionLadderBuiltScheduled", { count: assets.scheduled_contents }),
    assets.brand_identity && t("integrated.retentionLadderBuiltBrand"),
  ].filter((line): line is string => Boolean(line));
  if (lines.length === 0) return null;
  return (
    <div className="rounded-lg border border-purple-deep/25 bg-purple-deep/5 px-3 py-2 text-sm">
      <div className="font-medium">{t("integrated.retentionLadderBuiltTitle")}</div>
      <ul className="mt-1 list-disc pl-5 text-ink-2">
        {lines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

const CONFETTI_COLORS = ["#a576ff", "#76e6fa", "#7c5cff", "#10b981", "#f59e0b", "#5c39c8"];

/** The closing screen: a celebration with "Abrir agora" for a gift or the Premium, plain otherwise. */
function Celebration({
  kept,
  money,
  onOpen,
  onClose,
}: {
  kept: Kept;
  /** Null hides prices (not paying in reais). */
  money: ((cents: number) => string) | null;
  onOpen: (url: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  if (kept.kind === "plain") {
    return (
      <>
        <div className="text-lg font-semibold pr-8">{kept.title}</div>
        <p className="text-sm text-ink-2">{kept.body}</p>
        <div className="flex justify-end">
          <button type="button" className="btn-primary" onClick={onClose}>
            {t("integrated.retentionLadderClose")}
          </button>
        </div>
      </>
    );
  }
  const title =
    kept.kind === "gift"
      ? t("integrated.retentionLadderCelebrateGiftTitle")
      : t("integrated.retentionLadderCelebratePremiumTitle", { until: kept.until });
  const subtitle =
    kept.kind === "gift" ? t("integrated.retentionLadderCelebrateGiftSubtitle", { name: kept.name }) : null;
  const value =
    money && kept.kind === "gift" && kept.priceOnceCents
      ? t("integrated.retentionLadderCelebrateGiftValueOnce", { price: money(kept.priceOnceCents) })
      : money && kept.priceCents
        ? kept.kind === "gift"
          ? t("integrated.retentionLadderCelebrateGiftValue", { price: money(kept.priceCents) })
          : t("integrated.retentionLadderCelebratePremiumValue", { price: money(kept.priceCents) })
        : kept.kind === "gift"
          ? t("integrated.retentionLadderGiftFreeWhileSubscribed")
          : null;
  const openLabel =
    kept.kind === "gift" ? t("integrated.retentionLadderOpenGift") : t("integrated.retentionLadderOpenPremium");
  return (
    <div className="relative flex flex-col items-center gap-3 overflow-hidden pt-2 text-center">
      <div aria-hidden className="retention-confetti">
        {Array.from({ length: 18 }, (_, i) => (
          <span
            key={i}
            style={{
              left: `${(i * 53) % 100}%`,
              background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
              animationDelay: `${(i % 6) * 90}ms`,
            }}
          />
        ))}
      </div>
      <div
        className="flex h-16 w-16 items-center justify-center rounded-full text-white"
        style={{ background: "var(--grad-primary)" }}
      >
        <PartyPopper className="h-8 w-8" />
      </div>
      <div className="text-xl font-bold">{title}</div>
      {subtitle && <div className="text-base font-semibold">{subtitle}</div>}
      {value && <p className="text-sm font-semibold text-success">{value}</p>}
      <p className="text-sm text-ink-2">{t("integrated.retentionLadderCelebrateBody")}</p>
      <div className="flex w-full flex-col gap-2">
        {kept.openUrl && (
          <button type="button" className="btn-primary w-full" onClick={() => onOpen(kept.openUrl!)}>
            {openLabel}
          </button>
        )}
        <button type="button" className={kept.openUrl ? "btn-ghost w-full" : "btn-primary w-full"} onClick={onClose}>
          {kept.openUrl ? t("integrated.retentionLadderLater") : t("integrated.retentionLadderClose")}
        </button>
      </div>
    </div>
  );
}
